import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Disclaimer } from "@/components/Shared";
import ConsentCheckbox from "@/components/ConsentCheckbox";
import {
  Sparkles, Users, ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle,
  Trophy, Heart, Home as HomeIcon, DollarSign, Clock, ShieldCheck, FileText,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AGE_PENSION = 67; // Current Age Pension age (2026)

// Visa knowledge base — Australian Parent Visas · fee schedule effective 1 July 2026
const VISAS = [
  { code: "864", name: "Contributory Aged Parent Visa (Subclass 864)", type: "onshore",  pr: true,  temp: false, aged: true,  fee: 52810, months_low: 6,  months_high: 12, best_for: "Onshore parents at Age Pension age wanting PR fast." },
  { code: "884", name: "Contributory Aged Parent (Temporary) Visa (Subclass 884)", type: "onshore",  pr: false, temp: true,  aged: true,  fee: 34755, months_low: 6,  months_high: 12, best_for: "Onshore aged parent — temporary before 864." },
  { code: "804", name: "Aged Parent Visa (Subclass 804)", type: "onshore",  pr: true,  temp: false, aged: true,  fee: 6300,  months_low: 240, months_high: 360, best_for: "Onshore aged parent — cheapest but very long wait." },
  { code: "143", name: "Contributory Parent Visa (Subclass 143)", type: "offshore", pr: true,  temp: false, aged: false, fee: 52810, months_low: 12, months_high: 24, best_for: "Offshore parents seeking permanent residence quickly." },
  { code: "173", name: "Contributory Parent (Temporary) Visa (Subclass 173)", type: "offshore", pr: false, temp: true,  aged: false, fee: 34755, months_low: 12, months_high: 24, best_for: "Offshore parents — 2-yr temporary before 143." },
  { code: "103", name: "Parent Visa (Subclass 103)", type: "offshore", pr: true,  temp: false, aged: false, fee: 6300,  months_low: 240, months_high: 360, best_for: "Offshore parent — very low cost, extremely long wait." },
  { code: "870", name: "Sponsored Parent (Temporary) Visa (Subclass 870)", type: "any", pr: false, temp: true, aged: false, fee: 12030, months_low: 3, months_high: 12, best_for: "Longer visits (3 or 5 years) — no PR, sponsor income required." },
  { code: "600", name: "Visitor Visa (Subclass 600 — Sponsored Family)", type: "any", pr: false, temp: true, aged: false, fee: 250, months_low: 1, months_high: 3, best_for: "Short family visit — up to 12 months." },
];

const PRIORITIES = [
  { v: "fast",       l: "Fastest pathway" },
  { v: "cheap",      l: "Lowest government fees" },
  { v: "pr",         l: "Permanent Residency" },
  { v: "long_stay",  l: "Long stay with family" },
  { v: "low_wait",   l: "Lowest waiting time" },
  { v: "flexibility",l: "Maximum flexibility" },
  { v: "work",       l: "Can work in Australia" },
  { v: "medicare",   l: "Can access Medicare" },
];

const SPONSOR_OPTS = [
  { v: "citizen", l: "Yes — I'm an Australian citizen" },
  { v: "pr",      l: "Yes — I'm an Australian permanent resident" },
  { v: "nz",      l: "Yes — I'm an eligible New Zealand citizen" },
  { v: "none",    l: "No — none of the above (or unsure)" },
];

const RESIDENCE_OPTS = [
  { v: "lt4", l: "Less than 4 years" },
  { v: "4_5", l: "4–5 years" },
  { v: "gt5", l: "More than 5 years (I'm settled here)" },
];

const BUDGET_OPTS = [
  { v: "under_10k", l: "Under AUD 10,000 — very low budget" },
  { v: "10_30k",    l: "AUD 10,000 – 30,000" },
  { v: "over_30k",  l: "AUD 30,000 – 50,000" },
  { v: "no_limit",  l: "Over AUD 50,000 / no strict limit" },
];

const YN = [{ v: "yes", l: "Yes" }, { v: "no", l: "No" }];

function scoreVisas(input) {
  const results = [];
  for (const v of VISAS) {
    let score = 40;
    const why = [];
    // Sponsor eligibility gate
    if (input.sponsor_status === "none") { score -= 60; why.push("✗ Sponsor not currently eligible"); }
    // Aged parent match
    if (v.aged && input.aged_parent_eligible) { score += 15; why.push("✓ Parent meets Age Pension age"); }
    if (v.aged && !input.aged_parent_eligible) { score -= 40; why.push("✗ Parent below Age Pension age — this visa not available"); }
    // Onshore/offshore
    if (v.type !== "any" && ((input.parent_in_australia === "yes" && v.type !== "onshore") || (input.parent_in_australia === "no" && v.type !== "offshore"))) {
      score -= 25; why.push("✗ Wrong location for this pathway");
    } else if (v.type !== "any") {
      why.push("✓ Correct location for this pathway");
    }
    // Balance of Family Test
    if (["143","173","103","864","884","804"].includes(v.code)) {
      if (input.bof_pass) { score += 15; why.push("✓ Balance of Family Test satisfied"); }
      else { score -= 30; why.push("✗ Balance of Family Test not satisfied"); }
    }
    // Priority alignment
    const pri = input.priorities || [];
    if (pri.includes("pr") && v.pr) { score += 8; why.push("✓ Provides permanent residence"); }
    if (pri.includes("fast") && v.months_high <= 24) { score += 8; why.push("✓ Relatively fast processing"); }
    if (pri.includes("low_wait") && v.months_high <= 24) { score += 6; }
    if (pri.includes("cheap") && v.fee <= 15000) { score += 6; why.push("✓ Lower government fees"); }
    if (pri.includes("long_stay") && v.code === "870") { score += 8; why.push("✓ 3 or 5-year sponsored visit"); }
    if (pri.includes("flexibility") && ["600","870"].includes(v.code)) { score += 4; }
    // Budget
    if (input.budget === "under_10k" && v.fee > 10000) { score -= 15; why.push("✗ Exceeds budget of AUD 10,000"); }
    if (input.budget === "10_30k" && v.fee > 30000) { score -= 10; why.push("✗ Exceeds budget of AUD 30,000"); }
    // Risks
    if (input.previous_refusal === "yes") { score -= 6; }
    if (input.section_48 === "yes" && v.type === "onshore") { score -= 18; why.push("✗ Section 48 bar may prevent onshore lodgement"); }
    if (input.sponsor_meets_income === "no" && v.code === "870") { score -= 25; why.push("✗ Sponsor income requirement not met for 870"); }
    score = Math.max(0, Math.min(100, Math.round(score)));
    results.push({ ...v, score, why });
  }
  return results.sort((a, b) => b.score - a.score);
}

function readinessScore(input) {
  let s = 50;
  if (input.sponsor_status !== "none") s += 10;
  if (input.sponsor_meets_income !== "no") s += 8;
  if (input.bof_pass) s += 12;
  if (input.aged_parent_eligible) s += 6;
  if (input.previous_refusal === "no") s += 5;
  if (input.section_48 === "no") s += 4;
  if (input.health_issues === "no") s += 3;
  if (input.character_issues === "no") s += 2;
  return Math.min(100, s);
}

export default function ParentVisaFinder() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    sponsor_status: "", sponsor_name: "", sponsor_email: "", sponsor_phone: "",
    sponsor_age: "", residence: "", household_income: "",
    parent_name: "", parent_age: "", parent_country: "", parent_in_australia: "no",
    total_children: 2, children_in_au: 1,
    priorities: [], budget: "",
    previous_refusal: "no", section_48: "no", health_issues: "no", character_issues: "no",
    sponsor_debt: "no", sponsor_meets_income: "yes",
  });
  const [consent, setConsent] = useState(false);
  const [done, setDone] = useState(null);
  const u = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const bof_pass = useMemo(() => Number(form.children_in_au || 0) * 2 >= Number(form.total_children || 0), [form.children_in_au, form.total_children]);
  const aged_parent_eligible = useMemo(() => Number(form.parent_age || 0) >= AGE_PENSION, [form.parent_age]);

  const next = () => {
    if (step === 0) {
      if (!form.sponsor_status) { toast.error("Please choose sponsor status"); return; }
      if (form.sponsor_status === "none") { toast.info("Sponsor is not currently eligible. See visitor visa options below."); }
      if (!form.sponsor_name?.trim() || !form.sponsor_email?.trim()) { toast.error("Please enter sponsor name and email"); return; }
    }
    if (step === 1 && (!form.parent_name?.trim() || !form.parent_age)) { toast.error("Please enter parent name and age"); return; }
    setStep((s) => Math.min(4, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const runAssessment = async () => {
    if (!consent) { toast.error("Please tick consent"); return; }
    const input = { ...form, bof_pass, aged_parent_eligible };
    const rankings = scoreVisas(input);
    const readiness = readinessScore(input);
    const payload = {
      ...form,
      bof_test_result: bof_pass ? "Pass" : "Fail",
      aged_parent_eligible: aged_parent_eligible ? "Yes" : "No",
      rankings: rankings.slice(0, 3).map((r) => ({ code: r.code, name: r.name, score: r.score })),
      readiness_score: readiness,
      consent: true,
      consent_timestamp: new Date().toISOString(),
    };
    try {
      await axios.post(`${API}/parent-visa/submit`, payload);
      setDone({ rankings, readiness });
      toast.success("Assessment complete!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    }
  };

  return (
    <div data-testid="parent-finder-page" className="bg-white">

      {/* HERO */}
      <section className="relative bg-gradient-to-br from-[#29324A] via-[#1B1430] to-[#29324A] text-white overflow-hidden grain">
        <span aria-hidden className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89]" />
        <span aria-hidden className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#C43A89]/25 blur-3xl" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 relative">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
            <Sparkles className="h-3 w-3" /> Parent Visa Pathway Assessment
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-[3.2rem] leading-[1.05] tracking-tight mt-4">
            Find the right <span className="text-[#C43A89]">Parent Visa</span> for your family.
          </h1>
          <p className="text-[15px] text-slate-200 leading-relaxed mt-5 max-w-2xl">
            Answer 5 short, plain-English steps and our assessment engine will compare all 8 Australian Parent Visa pathways (subclasses 103, 143, 173, 804, 864, 884, 870, 600), explain what each means for your family, and rank them by fit, cost and processing time.
          </p>
          <div className="mt-6 flex flex-wrap gap-4 text-[12px] text-slate-300">
            <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-[#C43A89]" /> Takes ~3 minutes</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-[#C43A89]" /> No migration jargon</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-[#C43A89]" /> Private &amp; free</span>
          </div>
        </div>
      </section>

      {/* WIZARD or RESULTS */}
      <section className="py-12 md:py-16 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {!done ? (
            <div className="bg-white border border-slate-200 shadow-[0_25px_60px_-25px_rgba(41,50,74,0.25)]">
              {/* Progress */}
              <div className="p-5 border-b border-slate-100">
                <div className="flex items-center justify-between mb-2 text-[10.5px] tracking-wider uppercase text-slate-500 font-bold">
                  <span>Step {step + 1} of 5</span>
                  <span>{Math.round(((step + 1) / 5) * 100)}%</span>
                </div>
                <div className="h-1.5 w-full bg-slate-100">
                  <div className="h-full bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89] transition-all" style={{ width: `${((step + 1) / 5) * 100}%` }} />
                </div>
              </div>

              <div className="p-6 md:p-8 space-y-5">
                {/* STEP 0 — SPONSOR */}
                {step === 0 && (
                  <>
                    <StepIntro
                      icon={HomeIcon}
                      badge="Step 1 · About you"
                      title="First, tell us about you — the family member in Australia"
                      why="In Australian migration, a &quot;sponsor&quot; is simply the family member (usually the son or daughter) who is inviting the parent to come to Australia. To sponsor a parent visa you must be an Australian citizen, permanent resident or eligible New Zealand citizen who is settled here. If that&apos;s not you yet — no problem. Answer as best you can and we&apos;ll show the options anyway."
                    />

                    <div>
                      <Label className="text-[13.5px] font-semibold text-[#29324A] mb-1.5 block">Are you eligible to sponsor a parent to Australia?</Label>
                      <p className="text-[12px] text-slate-500 mb-2">This is the person in Australia inviting the parent — usually the parent&apos;s son or daughter.</p>
                      <Select value={form.sponsor_status} onValueChange={(v) => u("sponsor_status", v)}>
                        <SelectTrigger data-testid="pf-sponsor-status"><SelectValue placeholder="Choose one" /></SelectTrigger>
                        <SelectContent>{SPONSOR_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4 pt-2">
                      <F label="Your full name *" hint="The person in Australia."><Input value={form.sponsor_name} onChange={(e) => u("sponsor_name", e.target.value)} placeholder="e.g. Priya Sharma" /></F>
                      <F label="Your email *" hint="Where we send your personalised report."><Input type="email" value={form.sponsor_email} onChange={(e) => u("sponsor_email", e.target.value)} placeholder="you@example.com" /></F>
                      <F label="Your phone" hint="Optional — for follow-up only."><Input value={form.sponsor_phone} onChange={(e) => u("sponsor_phone", e.target.value)} placeholder="0400 000 000" /></F>
                      <F label="Your age" hint="Must be 18+ to sponsor a parent visa."><Input type="number" value={form.sponsor_age} onChange={(e) => u("sponsor_age", e.target.value)} placeholder="e.g. 34" /></F>
                      <F label="How long have you lived in Australia?" hint="You need to be &lsquo;settled&rsquo; in Australia to sponsor a parent — usually ~2+ years.">
                        <Select value={form.residence} onValueChange={(v) => u("residence", v)}>
                          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{RESIDENCE_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                        </Select>
                      </F>
                      <F label="Household taxable income (AUD)" hint="Only used if you might apply for Subclass 870 — it has a specific income threshold. Skip if unsure."><Input type="number" value={form.household_income} onChange={(e) => u("household_income", e.target.value)} placeholder="e.g. 90000" /></F>
                      <F label="Do you comfortably meet the 870 sponsor income test?" hint="Currently AUD 83,454.80 taxable income for the last full tax year. Only affects the 870 recommendation.">
                        <Select value={form.sponsor_meets_income} onValueChange={(v) => u("sponsor_meets_income", v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="yes">Yes — I meet it</SelectItem><SelectItem value="no">No / not sure</SelectItem></SelectContent>
                        </Select>
                      </F>
                    </div>
                  </>
                )}

                {/* STEP 1 — PARENT */}
                {step === 1 && (
                  <>
                    <StepIntro
                      icon={Heart}
                      badge="Step 2 · About your parent"
                      title="Now — tell us about the parent coming to Australia"
                      why="A parent&apos;s current age and location decide which visa subclasses are open to them. If they&apos;ve reached the Australian Age Pension age (67 years), a special set of &lsquo;Aged Parent&rsquo; visas becomes available with different processing rules. Their current country also determines if they should apply onshore (inside Australia) or offshore (from overseas)."
                    />
                    <div className="grid sm:grid-cols-2 gap-4">
                      <F label="Parent&apos;s full name *" hint="As it appears on their passport."><Input value={form.parent_name} onChange={(e) => u("parent_name", e.target.value)} placeholder="e.g. Meena Sharma" /></F>
                      <F label="Parent&apos;s current age *" hint="If they&apos;re 67 or older, extra Aged Parent visa options open up."><Input type="number" value={form.parent_age} onChange={(e) => u("parent_age", e.target.value)} placeholder="e.g. 66" /></F>
                      <F label="Country your parent lives in right now" hint="Their usual country of residence."><Input value={form.parent_country} onChange={(e) => u("parent_country", e.target.value)} placeholder="e.g. India · Bangladesh · Philippines" /></F>
                      <F label="Is your parent physically in Australia right now?" hint="Some visas must be lodged from inside Australia (onshore), others from outside (offshore).">
                        <Select value={form.parent_in_australia} onValueChange={(v) => u("parent_in_australia", v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{YN.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                        </Select>
                      </F>
                    </div>
                    {form.parent_age && (
                      <div className={`p-4 text-[13px] ${aged_parent_eligible ? "bg-emerald-50 border-l-4 border-emerald-500 text-emerald-900" : "bg-amber-50 border-l-4 border-amber-500 text-amber-900"}`}>
                        <div className="font-bold mb-1">{aged_parent_eligible ? "Aged Parent visas are available" : "Aged Parent visas are not open yet"}</div>
                        {aged_parent_eligible
                          ? `Your parent (age ${form.parent_age}) is at or above the current Australian Age Pension age of ${AGE_PENSION}, so Subclasses 804, 864 and 884 are on the table.`
                          : `Your parent (age ${form.parent_age}) is below the Age Pension age of ${AGE_PENSION}. Aged Parent visas (804 / 864 / 884) are not yet available — but Subclass 103, 143, 173 or the temporary 870 may still fit.`}
                      </div>
                    )}
                  </>
                )}

                {/* STEP 2 — BALANCE OF FAMILY */}
                {step === 2 && (
                  <>
                    <StepIntro
                      icon={Users}
                      badge="Step 3 · Balance of Family"
                      title="Where do your parent&apos;s children live?"
                      why="For most permanent Parent Visas, Australia uses a rule called the Balance of Family Test. It simply asks: are at least half of the parent&apos;s children permanently living in Australia (or is Australia the country with the most children)? Include ALL children — biological, adopted and step-children — no matter their age. If this rule isn&apos;t met, permanent Parent Visas are usually not available, but temporary options (870 or 600) still are."
                    />
                    <div className="grid sm:grid-cols-2 gap-4">
                      <F label="How many children does your parent have in total?" hint="Include biological, adopted and step-children — worldwide, of any age.">
                        <Input type="number" min="1" value={form.total_children} onChange={(e) => u("total_children", e.target.value)} />
                      </F>
                      <F label="How many of those children live in Australia permanently?" hint="&lsquo;Permanently&rsquo; means Australian citizens, permanent residents, or eligible NZ citizens.">
                        <Input type="number" min="0" value={form.children_in_au} onChange={(e) => u("children_in_au", e.target.value)} />
                      </F>
                    </div>
                    <div className={`p-4 text-[13px] ${bof_pass ? "bg-emerald-50 border-l-4 border-emerald-500 text-emerald-900" : "bg-rose-50 border-l-4 border-rose-500 text-rose-900"}`}>
                      <div className="font-bold mb-1">{bof_pass ? "Balance of Family Test — PASS" : "Balance of Family Test — likely FAIL"}</div>
                      {bof_pass
                        ? `Great — at least half of the ${form.total_children || 0} children are settled in Australia. All permanent Parent Visa subclasses remain open.`
                        : `Of the ${form.total_children || 0} children in total, only ${form.children_in_au || 0} live in Australia. Permanent Parent Visas may not be available — but Subclass 870 (up to 5 yrs) and Visitor 600 (up to 12 months) don&rsquo;t use this test.`}
                    </div>
                  </>
                )}

                {/* STEP 3 — PRIORITIES + BUDGET */}
                {step === 3 && (
                  <>
                    <StepIntro
                      icon={Trophy}
                      badge="Step 4 · What matters most"
                      title="What&apos;s most important to your family?"
                      why="Every family has different priorities. Some want the fastest path to permanent residency (even if it&apos;s expensive), some want the lowest cost (even if it means a long wait), others just want a way for parents to visit for longer stretches. Tick the things that matter — you can choose as many as you like — and tell us your rough budget. This is used only to rank the visas most likely to suit you."
                    />
                    <div>
                      <Label className="text-[13.5px] font-semibold text-[#29324A] mb-1.5 block">What matters most for your family? (tick any that apply)</Label>
                      <p className="text-[12px] text-slate-500 mb-3">Pick as many as you like. There&apos;s no right or wrong answer here.</p>
                      <div className="flex flex-wrap gap-2">
                        {PRIORITIES.map((p) => {
                          const active = form.priorities.includes(p.v);
                          return (
                            <button key={p.v} type="button" onClick={() => u("priorities", active ? form.priorities.filter((x) => x !== p.v) : [...form.priorities, p.v])}
                              className={`text-[12px] font-medium px-3 py-1.5 border transition-colors ${active ? "bg-[#29324A] border-[#29324A] text-white" : "bg-white border-slate-200 text-slate-700 hover:border-[#29324A]"}`}>
                              {p.l}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <F label="Rough budget for government visa fees (per parent, both instalments)" hint="Parent visa fees range from ~AUD 250 (visitor) to ~AUD 48,640 (Contributory 143). Pick the band that fits.">
                      <Select value={form.budget} onValueChange={(v) => u("budget", v)}>
                        <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{BUDGET_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </F>
                  </>
                )}

                {/* STEP 4 — RISK ASSESSMENT + CONSENT */}
                {step === 4 && (
                  <>
                    <StepIntro
                      icon={ShieldCheck}
                      badge="Step 5 · Anything to flag?"
                      title="Are there any circumstances we should know about?"
                      why="These questions help us flag any complications early so your report is realistic. Answering &lsquo;yes&rsquo; to something doesn&rsquo;t mean an application will fail — it just means we should look at it carefully. If you&apos;re unsure about any question, pick &lsquo;No&rsquo; and mention it during your consultation."
                    />
                    <div className="grid sm:grid-cols-2 gap-3">
                      {[
                        ["previous_refusal", "Has your parent had an Australian visa refused before?", "Any previous refusal — student, visitor, PR — may need explanation."],
                        ["section_48", "Was that refusal made while they were inside Australia?", "This can trigger a &lsquo;Section 48 bar&rsquo; that limits onshore lodgement."],
                        ["health_issues", "Does your parent have significant health concerns?", "Serious/costly conditions may require a health waiver."],
                        ["character_issues", "Any criminal record or character concerns?", "Includes convictions, deportations, or serious civil matters."],
                        ["sponsor_debt", "Does the sponsor have outstanding debt to the Australian Government?", "e.g. unpaid Centrelink debt — can block sponsorship."],
                      ].map(([k, label, hint]) => (
                        <F key={k} label={label} hint={hint}>
                          <Select value={form[k]} onValueChange={(v) => u(k, v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{YN.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                          </Select>
                        </F>
                      ))}
                    </div>
                    <ConsentCheckbox value={consent} onChange={setConsent} testid="pf-consent"
                      label="I understand this is a general information tool only. Recommendations do not guarantee visa grants. I consent to Shamima Yasmin Anwar contacting me about my enquiry." />
                  </>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <Button type="button" variant="ghost" disabled={step === 0} onClick={back}><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
                  {step < 4 ? (
                    <Button type="button" onClick={next} className="bg-[#29324A] hover:bg-[#1f2638] text-white rounded-none px-6" data-testid="pf-next">Next <ArrowRight className="h-4 w-4 ml-1" /></Button>
                  ) : (
                    <Button type="button" onClick={runAssessment} disabled={!consent} className="bg-gradient-to-br from-[#29324A] to-[#C43A89] hover:from-[#1f2638] hover:to-[#a22b6e] text-white rounded-none px-6 btn-shimmer disabled:opacity-50" data-testid="pf-run">
                      Generate my pathway report <Sparkles className="h-4 w-4 ml-1" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <ResultsPage rankings={done.rankings} readiness={done.readiness} restart={() => { setDone(null); setStep(0); }} />
          )}
        </div>
      </section>

      <Disclaimer />
    </div>
  );
}

function F({ label, hint, children }) {
  return (
    <div>
      <Label className="text-[12.5px] font-semibold text-[#29324A] mb-1 block leading-snug">{label}</Label>
      {hint && <p className="text-[11px] text-slate-500 mb-1.5 leading-snug" dangerouslySetInnerHTML={{ __html: hint }} />}
      {children}
    </div>
  );
}

function StepIntro({ icon: Icon, badge, title, why }) {
  return (
    <div className="bg-gradient-to-br from-[#F8F4FB] to-white border-l-4 border-[#29324A] p-5 md:p-6 mb-4">
      <div className="flex items-start gap-4">
        {Icon && (
          <span className="shrink-0 h-11 w-11 rounded-full bg-white border border-[#29324A]/20 flex items-center justify-center">
            <Icon className="h-5 w-5 text-[#29324A]" />
          </span>
        )}
        <div>
          <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#C43A89]">{badge}</div>
          <h2 className="font-display text-xl md:text-[1.6rem] text-[#29324A] mt-1 leading-tight">{title}</h2>
          <p className="text-[12.5px] text-slate-600 leading-relaxed mt-2" dangerouslySetInnerHTML={{ __html: `<strong class='text-[#29324A]'>Why we ask this →</strong> ${why}` }} />
        </div>
      </div>
    </div>
  );
}

function ResultsPage({ rankings, readiness, restart }) {
  const band = readiness >= 85 ? { tone: "emerald", label: "Excellent Candidate" } : readiness >= 65 ? { tone: "amber", label: "Good Candidate — Some Preparation Needed" } : readiness >= 45 ? { tone: "orange", label: "Preparation Required" } : { tone: "rose", label: "Professional Review Recommended" };
  const toneMap = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    orange:  "bg-orange-50 border-orange-200 text-orange-900",
    rose:    "bg-rose-50 border-rose-200 text-rose-900",
  };
  const barMap = { emerald: "bg-emerald-600", amber: "bg-amber-600", orange: "bg-orange-600", rose: "bg-rose-600" };

  return (
    <div className="space-y-6" data-testid="pf-results">
      <div className="grid md:grid-cols-3 gap-4">
        <div className={`${toneMap[band.tone]} border p-6`}>
          <div className="text-[10px] font-bold tracking-[0.22em] uppercase mb-2">Readiness score</div>
          <div className="font-display text-5xl">{readiness}<span className="text-2xl text-slate-500">/100</span></div>
          <div className="mt-2 text-sm">{band.label}</div>
          <div className="mt-3 h-2 w-full bg-white border border-slate-200"><div className={`h-full ${barMap[band.tone]}`} style={{ width: `${readiness}%` }} /></div>
        </div>
        <div className="md:col-span-2 bg-white border border-slate-200 p-6">
          <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#C43A89] mb-2">Top recommendation</div>
          <h3 className="font-display text-2xl text-[#29324A] leading-tight">{rankings[0].name}</h3>
          <div className="mt-2 inline-flex items-center gap-2 text-[13px] text-slate-600">
            <Trophy className="h-4 w-4 text-[#C43A89]" /> Score: <strong className="text-[#29324A]">{rankings[0].score}/100</strong>
            <span className="mx-2">·</span>
            <DollarSign className="h-4 w-4 text-slate-400" /> ~AUD ${rankings[0].fee.toLocaleString()}
            <span className="mx-2">·</span>
            <Clock className="h-4 w-4 text-slate-400" /> {rankings[0].months_low}–{rankings[0].months_high} months
          </div>
          <p className="text-[13px] text-slate-700 mt-3">{rankings[0].best_for}</p>
        </div>
      </div>

      <div>
        <h3 className="font-display text-xl text-[#29324A] mb-3">All Parent Visa options — ranked for your family</h3>
        <div className="grid gap-2">
          {rankings.map((v, i) => (
            <div key={v.code} className={`bg-white border p-4 ${i === 0 ? "border-[#C43A89]" : "border-slate-200"}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-[10px] font-bold tracking-wider uppercase text-slate-500">#{i + 1} · Subclass {v.code}</div>
                  <div className="font-display text-base text-[#29324A]">{v.name}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-2xl text-[#29324A]">{v.score}<span className="text-sm text-slate-400">/100</span></div>
                  <div className="text-[11px] text-slate-500">AUD ${v.fee.toLocaleString()} · {v.months_low}–{v.months_high} mo</div>
                </div>
              </div>
              <ul className="mt-2 space-y-1 text-[12px] text-slate-700">
                {v.why.slice(0, 4).map((w, j) => (<li key={j} className={w.startsWith("✓") ? "text-emerald-700" : w.startsWith("✗") ? "text-rose-700" : ""}>{w}</li>))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[#F8F4FB] border-l-4 border-[#29324A] p-5">
        <div className="text-[10px] tracking-wider uppercase text-[#29324A] font-bold mb-1">Next step</div>
        <p className="text-[13.5px] text-slate-700">A member of our migration team will review your assessment and follow up within 1 business day. Book a paid consultation for a detailed strategy meeting with our Registered Migration Agent.</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link to="/book-consultation" className="bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-wider uppercase px-5 py-3 inline-flex items-center gap-1.5" data-testid="pf-book">Book consultation <ArrowRight className="h-3.5 w-3.5" /></Link>
          <Link to="/parent-visas" className="bg-white border border-slate-300 hover:border-[#29324A] text-slate-700 text-xs font-bold tracking-wider uppercase px-5 py-3 inline-flex items-center gap-1.5">See all Parent Visas <FileText className="h-3.5 w-3.5" /></Link>
          <button onClick={restart} className="text-xs font-bold tracking-wider uppercase text-slate-500 hover:text-[#29324A] px-4 py-3">Start over</button>
        </div>
      </div>

      <p className="text-[11px] text-slate-500 leading-relaxed flex items-start gap-2">
        <AlertTriangle className="h-3.5 w-3.5 text-[#C43A89] mt-0.5 shrink-0" />
        This tool is general information only, based on publicly available Department of Home Affairs data. Government fees, thresholds, Age Pension age and processing times change frequently. Recommendations do not guarantee visa eligibility or grant. Always confirm details on the official Home Affairs website or with a Registered Migration Agent before applying.
      </p>
    </div>
  );
}
